import {
  Component,
  Input,
  signal,
  computed,
  ElementRef,
  HostListener,
  inject,
  forwardRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type PickerMode = 'days' | 'months' | 'years';

@Component({
  selector: 'app-date-picker',
  imports: [CommonModule],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePicker),
      multi: true
    }
  ]
})
export class DatePicker implements ControlValueAccessor {
  private elementRef = inject(ElementRef);

  @Input() placeholder = 'Select Date';
  @Input() minDate: string | null = null;
  @Input() format = 'DD-MM-YYYY';
  @Input() dropdownAlign: 'left' | 'right' = 'left';

  value = signal<Date | null>(null);
  isOpen = signal(false);
  mode = signal<PickerMode>('days');
  viewDate = signal(new Date());

  monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  yearRangeStart = computed(() => {
    const year = this.viewDate().getFullYear();
    return Math.floor(year / 12) * 12;
  });

  yearOptions = computed(() => {
    const start = this.yearRangeStart();
    return Array.from({ length: 12 }, (_, i) => start + i);
  });

  onChange: (value: any) => void = () => {
    // Default empty handler
  };
  onTouched: () => void = () => {
    // Default empty handler
  };

  formattedDate = computed(() => {
    const val = this.value();
    if (!val) return '';

    const d = val.getDate().toString().padStart(2, '0');
    const m = (val.getMonth() + 1).toString().padStart(2, '0');
    const y = val.getFullYear();

    return `${d}-${m}-${y}`;
  });

  calendarDays = computed(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startingDayOfWeek = firstDayOfMonth.getDay();
    const days: any[] = [];

    // Prev month days
    const prevMonthLastDay = new Date(year, month, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay.getDate() - i),
        currentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push({
        date: d,
        currentMonth: true,
        isToday: this.isToday(d)
      });
    }

    // Next month days to fill 42 cells (6 rows)
    const totalDaysSoFar = days.length;
    for (let i = 1; i <= 42 - totalDaysSoFar; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        currentMonth: false
      });
    }

    return days;
  });

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closePicker();
    }
  }

  togglePicker() {
    if (this.isOpen()) {
      this.closePicker();
    } else {
      this.openPicker();
    }
  }

  openPicker() {
    this.isOpen.set(true);
    this.mode.set('days');
    const val = this.value();
    if (val) {
      this.viewDate.set(new Date(val));
    } else {
      this.viewDate.set(new Date());
    }
  }

  closePicker() {
    this.isOpen.set(false);
    this.mode.set('days');
    this.onTouched();
  }

  toggleMode() {
    if (this.mode() === 'days') this.mode.set('months');
    else if (this.mode() === 'months') this.mode.set('years');
    else this.mode.set('days');
  }

  prevPage() {
    const current = this.viewDate();
    if (this.mode() === 'days') {
      this.viewDate.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
    } else if (this.mode() === 'months') {
      this.viewDate.set(new Date(current.getFullYear() - 1, current.getMonth(), 1));
    } else {
      this.viewDate.set(new Date(current.getFullYear() - 12, current.getMonth(), 1));
    }
  }

  nextPage() {
    const current = this.viewDate();
    if (this.mode() === 'days') {
      this.viewDate.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
    } else if (this.mode() === 'months') {
      this.viewDate.set(new Date(current.getFullYear() + 1, current.getMonth(), 1));
    } else {
      this.viewDate.set(new Date(current.getFullYear() + 12, current.getMonth(), 1));
    }
  }

  selectDate(date: Date) {
    if (this.isDisabled(date)) return;
    this.value.set(new Date(date));
    this.onChange(this.toISODate(date));
    this.closePicker();
  }

  selectMonth(month: number) {
    const current = this.viewDate();
    this.viewDate.set(new Date(current.getFullYear(), month, 1));
    this.mode.set('days');
  }

  selectYear(year: number) {
    const current = this.viewDate();
    this.viewDate.set(new Date(year, current.getMonth(), 1));
    this.mode.set('months');
  }

  clearValue(event?: Event) {
    if (event) event.stopPropagation();
    this.value.set(null);
    this.onChange(null);
    if (!event) this.closePicker(); // if from footer
  }

  setToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (this.isDisabled(today)) {
      // If today is disabled, just jump to current month
      this.viewDate.set(new Date());
      this.mode.set('days');
    } else {
      this.selectDate(today);
    }
  }

  isSelected(date: Date) {
    const val = this.value();
    if (!val) return false;
    return (
      date.getDate() === val.getDate() &&
      date.getMonth() === val.getMonth() &&
      date.getFullYear() === val.getFullYear()
    );
  }

  isDisabled(date: Date) {
    if (!this.minDate) return false;
    const min = new Date(this.minDate);
    min.setHours(0, 0, 0, 0);
    const check = new Date(date);
    check.setHours(0, 0, 0, 0);
    return check.getTime() < min.getTime();
  }

  isToday(date: Date) {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  toISODate(date: Date) {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // ControlValueAccessor
  writeValue(value: any): void {
    if (value) {
      if (typeof value === 'string') {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          this.value.set(d);
        }
      } else if (value instanceof Date) {
        this.value.set(value);
      }
    } else {
      this.value.set(null);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(): void {
    // Implement if needed
  }
}
